import React, { ReactNode, useEffect, useId, useRef, useState } from 'react';
import styled from 'styled-components';
import { ReportGridAppearance, ReportGridDensity, ReportGridInitialExpansion, ReportGridTitleOptions } from './types';

/** Mantém o texto parcial enquanto o usuário digita; limites só corrigem o valor no blur. */
function NumericSettingInput({ value, min, max, onChange, optional = false, onClear }: {
  value?: number; min: number; max: number; onChange: (value: number) => void; optional?: boolean; onClear?: () => void;
}) {
  const [draft, setDraft] = useState(value === undefined ? '' : String(value));
  const focused = useRef(false);
  useEffect(() => { if (!focused.current) setDraft(value === undefined ? '' : String(value)); }, [value]);
  const commit = () => {
    focused.current = false;
    if (optional && !draft.trim()) { setDraft(''); onClear?.(); return; }
    const parsed = draft.trim() ? Number(draft) : NaN;
    const next = Number.isFinite(parsed) ? Math.max(min, Math.min(max, parsed)) : value ?? min;
    setDraft(String(next));
    if (next !== value) onChange(next);
  };
  return <input type="number" min={min} max={max} value={draft} placeholder={optional ? 'Sem limite' : undefined}
    onFocus={() => { focused.current = true; }} onBlur={commit}
    onKeyDown={event => { if (event.key === 'Enter') event.currentTarget.blur(); }}
    onChange={event => {
      setDraft(event.target.value);
      if (optional && !event.target.value) onClear?.();
      const next = event.target.valueAsNumber;
      // Valores válidos continuam atualizando imediatamente, inclusive pelas setas.
      if (Number.isFinite(next) && next >= min && next <= max) onChange(next);
    }} />;
}

export interface ReportGridSettings {
  initial: 'depth' | 'expanded' | 'collapsed';
  search: boolean;
  height: number;
  density?: ReportGridDensity;
  showRowTotals?: boolean;
  showColumnTotals?: boolean;
  /** @deprecated Legacy fallback; prefer the independent axis options. */
  showTotals?: boolean;
  showBalance?: boolean;
  appearance: Partial<ReportGridAppearance>;
  title: ReportGridTitleOptions;
}
export const defaultReportGridSettings: ReportGridSettings = {
  initial: 'collapsed', search: true, height: 560, density: 'comfortable', showRowTotals: true, showColumnTotals: true, showBalance: true,
  appearance: { fontFamily: 'inherit', fontSize: '13px', rowHeight: '43px', columnWidth: '138px', rowHeaderWidth: '340px',
    headerHeight: '42px', striped: false, stripeBackground: '#f7faf8', stripeText: '#203734',
    accent: '#14675e', background: '#ffffff', text: '#203734', header: '#f3f7f5', border: '#dfe8e4',
    rowSubtotalBackground: '#eef5f1', rowSubtotalText: '#203734', columnSubtotalBackground: '#eef5f1', columnSubtotalText: '#203734',
    total: '#dcece5', totalText: '#203734', negativeText: '#b13b39', highlightBackground: '#fff0b3', highlightText: '#594000', cellHighlightBackground: '#ffd45a', cellHighlightText: '#302300', cellHighlightBorder: '#a66b00' },
  title: { visible: true, text: '', subtitle: '', align: 'left', fontSize: '14px', fontWeight: 600, color: '#667a76', background: '#ffffff' },
};
export const settingsInitialExpansion = (setting: ReportGridSettings['initial']): ReportGridInitialExpansion =>
  setting === 'depth' ? { rows: 1, columns: 'all' } : setting;

const Panel = styled.div`
  border: 1px solid #dfe8e4; border-radius: 12px; background: #fff; margin: 0 0 20px;
  .tabs { display: flex; gap: 6px; flex-wrap: wrap; padding: 12px; border-bottom: 1px solid #dfe8e4; }
  .tabs button { font: inherit; cursor: pointer; border: 1px solid #ccdcd4; border-radius: 6px; padding: 9px 12px; background: #f8faf7; color: #203734; }
  .tabs button[aria-selected=true] { background: #14675e; color: white; }
  [hidden] { display: none; }
  > div { padding: 0 18px 18px; }
  fieldset { border: 0; border-top: 1px solid #e4ece7; margin: 12px 0 0; padding: 14px 0 0; min-width: 0; }
  legend { padding: 0 8px 0 0; font-size: 13px; font-weight: 600; color: #14675e; }
  .grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(210px, 1fr)); gap: 14px; }
  label { font-size: 12px; color: #4f6860; display: flex; gap: 7px; flex-direction: column; }
  label.check { flex-direction: row; align-items: center; }
  .report-grid-data-settings label {
    display: grid; grid-template-columns: minmax(100px, 140px) minmax(0, 1fr);
    align-items: center; gap: 12px; min-width: 0;
  }
  .report-grid-data-settings label > input, .report-grid-data-settings label > select { min-width: 0; }
  @media(max-width: 650px) {
    .report-grid-data-settings label { grid-template-columns: minmax(0, 1fr); gap: 6px; }
  }
  input:not([type=checkbox]), select {
    box-sizing: border-box; font: inherit; width: 100%; min-width: 0; min-height: 38px;
    padding: 9px 10px; color: #213c37; background: #fbfcfa;
    border: 1px solid #cadbd0; border-radius: 6px;
  }
  select {
    appearance: none; cursor: pointer; padding-right: 34px;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23526b63' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
    background-repeat: no-repeat; background-position: right 10px center; background-size: 16px;
  }
  select:hover:not(:disabled) { border-color: #93b9a9; }
  select:focus-visible, input:focus-visible { outline: 2px solid #14675e; outline-offset: 2px; }
  select:disabled, fieldset:disabled select { cursor: default; opacity: .55; }
  @media (forced-colors: active) { select { appearance: auto; background-image: none; } }
  input[type=color] { padding: 3px; }
  p { font-size: 12px; line-height: 1.7; color: #667a76; margin: 12px 0 0; }
  && {
    background: ${({ theme }) => theme.modal || '#fff'};
    color: ${({ theme }) => theme.color || '#212121'};
    border-color: ${({ theme }) => theme.containerLine || '#dfe8e4'};
  }
  && .tabs, && fieldset { border-color: ${({ theme }) => theme.containerLine || '#dfe8e4'}; }
  && .tabs button {
    background: ${({ theme }) => theme.container || '#fafafa'};
    color: ${({ theme }) => theme.color || '#212121'};
    border-color: ${({ theme }) => theme.containerLine || '#ddd'};
  }
  && .tabs button[aria-selected=true] {
    background: ${({ theme }) => theme.primary || '#5602c1'};
    color: ${({ theme }) => theme.white || '#fff'};
  }
  && legend { color: ${({ theme }) => theme.primary || '#5602c1'}; }
  && label, && p { color: ${({ theme }) => theme.color || '#212121'}; }
  && input:not([type=checkbox]), && select {
    color: ${({ theme }) => theme.color || '#212121'};
    background-color: ${({ theme }) => theme.container || '#fafafa'};
    border-color: ${({ theme }) => theme.containerLine || '#ddd'};
  }
  && input[type=checkbox] { accent-color: ${({ theme }) => theme.primary || '#5602c1'}; }
  && button:focus-visible, && input:focus-visible, && select:focus-visible {
    outline: 3px solid ${({ theme }) => theme.warning || '#ffd54f'};
    outline-offset: 2px;
  }
`;
const palettes = {
  green: { accent: '#14675e', header: '#f3f7f5', rowSubtotalBackground: '#eef5f1', columnSubtotalBackground: '#e2f0eb', total: '#dcece5', stripeBackground: '#f7faf8' },
  blue: { accent: '#2456a6', header: '#eef3fc', rowSubtotalBackground: '#e5edfb', columnSubtotalBackground: '#dbe6fb', total: '#c6d8f5', stripeBackground: '#f4f7fe' },
  amber: { accent: '#855512', header: '#faf5e9', rowSubtotalBackground: '#f7ecd3', columnSubtotalBackground: '#f0e0b6', total: '#e9d399', stripeBackground: '#fcf9f0' },
};
export interface ReportGridSettingsFormProps {
  value: ReportGridSettings;
  onChange: (value: ReportGridSettings) => void;
  /** Formulário de dados fornecido pelo sistema hospedeiro, sem acoplamento a APIs. */
  dataControls?: ReactNode;
  filterControls?: ReactNode;
  controlled?: boolean;
  disabled?: boolean;
}
/** Controlled form with no modal, routing, persistence or dependency on the demo. */
export function ReportGridSettingsForm({ value, onChange: emitChange, controlled = false, disabled = false, dataControls, filterControls }: ReportGridSettingsFormProps) {
  const onChange = (next: ReportGridSettings) => { if (!disabled) emitChange(next); };
  value = { ...defaultReportGridSettings, ...value, showRowTotals: value.showRowTotals ?? value.showTotals ?? true, showColumnTotals: value.showColumnTotals ?? value.showTotals ?? true, appearance: { ...defaultReportGridSettings.appearance, ...value.appearance }, title: { ...defaultReportGridSettings.title, ...value.title } };
  const [tab, setTab] = useState(dataControls !== undefined ? -1 : 0);
  const id = useId();
  const tabs = useRef<(HTMLButtonElement | null)[]>([]);
  const tabNames = [...(dataControls !== undefined ? [{ name: 'Dados', index: -1 }] : []), ...(filterControls !== undefined ? [{ name: 'Filtros', index: -2 }] : []), ...['Comportamento e dimensões', 'Tipografia e cores', 'Título'].map((name, index) => ({ name, index }))];
  const appearance = <K extends keyof ReportGridAppearance>(key: K, next: ReportGridAppearance[K]) => { if (!disabled) onChange({ ...value, appearance: { ...value.appearance, [key]: next } }); };
  const title = <K extends keyof ReportGridTitleOptions>(key: K, next: ReportGridTitleOptions[K]) => { if (!disabled) onChange({ ...value, title: { ...value.title, [key]: next } }); };
  const size = (key: keyof ReportGridAppearance, label: string, min: number, max: number) => <label key={key}>{label} (px)
    <NumericSettingInput min={min} max={max} value={parseInt(String(value.appearance[key]), 10)}
      onChange={next => appearance(key, `${next}px`)} />
  </label>;
  const optionalSize = (key: 'cellMaxWidth' | 'cellMaxHeight', label: string) => <label>{label} (px)
    <NumericSettingInput optional min={16} max={2000}
      value={value.appearance[key] ? parseInt(value.appearance[key]!, 10) : undefined}
      onChange={next => appearance(key, `${next}px`)} onClear={() => appearance(key, undefined)} />
  </label>;
  const colors: [keyof ReportGridAppearance, string][] = [
    ['accent', 'Cor dos controles'], ['background', 'Fundo da tabela'], ['text', 'Texto da tabela'], ['header', 'Fundo dos cabeçalhos'], ['border', 'Bordas'],
    ['stripeBackground', 'Fundo do zebrado'], ['stripeText', 'Texto do zebrado'],
    ['rowSubtotalBackground', 'Subtotal das linhas: fundo'], ['rowSubtotalText', 'Subtotal das linhas: texto'],
    ['columnSubtotalBackground', 'Subtotal das colunas: fundo'], ['columnSubtotalText', 'Subtotal das colunas: texto'],
    ['cellHighlightBackground', 'Célula encontrada: fundo'], ['cellHighlightText', 'Célula encontrada: texto'], ['cellHighlightBorder', 'Célula encontrada: borda'],
    ['highlightBackground', 'Correspondências: fundo'], ['highlightText', 'Correspondências: texto'],
    ['total', 'Total geral: fundo'], ['totalText', 'Total geral: texto'], ['negativeText', 'Valores negativos'],
  ];
  return <Panel>
    <div className="tabs" role="tablist" aria-label="Configurações da tabela">
      {tabNames.map(({ name, index }, position) => <button key={name} type="button" role="tab" id={`${id}-tab-${index}`}
        ref={element => { tabs.current[position] = element; }}
        aria-selected={tab === index} aria-controls={`${id}-panel-${index}`} tabIndex={tab === index ? 0 : -1}
        onClick={() => setTab(index)} onKeyDown={event => {
          const next = event.key === 'ArrowRight' ? (position + 1) % tabNames.length : event.key === 'ArrowLeft' ? (position + tabNames.length - 1) % tabNames.length : event.key === 'Home' ? 0 : event.key === 'End' ? tabNames.length - 1 : null;
          if (next !== null) { event.preventDefault(); setTab(tabNames[next].index); tabs.current[next]?.focus(); }
        }}>{name}</button>)}
    </div><div>
    {dataControls !== undefined && <section className="report-grid-data-settings" role="tabpanel" id={`${id}-panel--1`} aria-labelledby={`${id}-tab--1`} hidden={tab !== -1}>
      <fieldset disabled={disabled}><legend>Dados</legend>{dataControls}</fieldset>
    </section>}
    {filterControls !== undefined && <section role="tabpanel" id={`${id}-panel--2`} aria-labelledby={`${id}-tab--2`} hidden={tab !== -2}>
      <fieldset disabled={disabled}><legend>Filtros</legend>{filterControls}</fieldset>
    </section>}
    <section role="tabpanel" id={`${id}-panel-0`} aria-labelledby={`${id}-tab-0`} hidden={tab !== 0}><fieldset disabled={disabled}><legend>Comportamento e dimensões</legend><div className="grid">
      <label>Visualização padrão<select value={value.density ?? 'comfortable'} onChange={e => onChange({ ...value, density: e.target.value as ReportGridDensity })}>
        <option value="comfortable">Confortável</option><option value="compact">Compacta (planilha)</option>
      </select></label>
      <label>Expansão inicial<select disabled={controlled} value={value.initial} onChange={e => onChange({ ...value, initial: e.target.value as ReportGridSettings['initial'] })}>
        <option value="depth">Primeiro nível das linhas</option><option value="expanded">Tudo expandido</option><option value="collapsed">Tudo recolhido</option>
      </select></label>
      <span></span>
      <span></span>
      <span></span>
      </div>
      <hr></hr>
      <div className="grid">
      <label className="check"><input type="checkbox" checked={value.showBalance ?? true} onChange={e => onChange({ ...value, showBalance: e.target.checked })} /> Mostrar linha de saldo</label>
      <label className="check"><input type="checkbox" checked={value.showRowTotals} onChange={e => onChange({ ...value, showRowTotals: e.target.checked })} /> Mostrar totais das linhas (coluna final)</label>
      <label className="check"><input type="checkbox" checked={value.showColumnTotals} onChange={e => onChange({ ...value, showColumnTotals: e.target.checked })} /> Mostrar totais das colunas (linha final)</label>
      <label className="check"><input type="checkbox" checked={value.search} onChange={e => onChange({ ...value, search: e.target.checked })} /> Campo de busca</label>
      <label className="check"><input type="checkbox" checked={!!value.appearance.striped} onChange={e => appearance('striped', e.target.checked)} /> Linhas zebradas</label>
      </div>
      <hr></hr>
      <div className="grid">
      <label>Altura máxima da tabela (px)<NumericSettingInput min={220} max={1000} value={value.height} onChange={height => onChange({ ...value, height })} /></label>
      {size('headerHeight', 'Altura dos cabeçalhos', 28, 190)}
      {size('rowHeaderWidth', 'Largura dos rótulos de linha', 240, 1000)}
      <span></span>
      <span></span>
      {size('columnWidth', 'Largura mínima das colunas', 100, 520)}
      {optionalSize('cellMaxWidth', 'Largura máxima (opcional)')}
      <span></span>
      <span></span>
      <span></span>
      {size('rowHeight', 'Altura mínima das linhas', 28, 190)}
      {optionalSize('cellMaxHeight', 'Altura máxima (opcional)')}  
      <span></span>
      <span></span>
      <span></span>    
    </div><p>A expansão inicial é reaplicada ao trocar esta opção na demonstração. Em modo controlado, o estado de expansão da aplicação tem prioridade. A busca revela os caminhos encontrados e mantém os totais completos da base.</p></fieldset></section>
    <section role="tabpanel" id={`${id}-panel-1`} aria-labelledby={`${id}-tab-1`} hidden={tab !== 1}><fieldset disabled={disabled}><legend>Tipografia e cores</legend><div className="grid">
      <label>Fonte<select value={value.appearance.fontFamily} onChange={e => appearance('fontFamily', e.target.value)}>
        <option value="inherit">Fonte do projeto</option><option value="Arial, sans-serif">Arial</option><option value="Georgia, serif">Georgia</option><option value="monospace">Monoespaçada</option>
      </select></label>
      {size('fontSize', 'Tamanho do texto', 11, 22)}
      <label>Aplicar paleta<select defaultValue="" onChange={e => { const palette = palettes[e.target.value as keyof typeof palettes]; if (palette) onChange({ ...value, appearance: { ...value.appearance, ...palette } }); e.currentTarget.value = ''; }}>
        <option value="" disabled>Selecione uma paleta</option><option value="green">Verde</option><option value="blue">Azul</option><option value="amber">Âmbar</option>
      </select></label>
      {colors.map(([key, label]) => <label key={key}>{label}<input type="color" value={String(value.appearance[key])} onChange={e => appearance(key, e.target.value)} /></label>)}
    </div></fieldset></section>
    <section role="tabpanel" id={`${id}-panel-2`} aria-labelledby={`${id}-tab-2`} hidden={tab !== 2}><fieldset disabled={disabled}><legend>Título</legend><div className="grid">
      <label className="check"><input type="checkbox" checked={value.title.visible !== false} onChange={e => title('visible', e.target.checked)} /> Mostrar título</label>
      <label>Texto do título<input value={value.title.text} placeholder="Usar o título do exemplo" onChange={e => title('text', e.target.value)} /></label>
      <label>Subtítulo<input value={value.title.subtitle} onChange={e => title('subtitle', e.target.value)} /></label>
      <label>Alinhamento<select value={value.title.align} onChange={e => title('align', e.target.value as ReportGridTitleOptions['align'])}><option value="left">Esquerda</option><option value="center">Centro</option><option value="right">Direita</option></select></label>
      <label>Tamanho do título (px)<NumericSettingInput min={11} max={32} value={parseInt(value.title.fontSize!, 10)} onChange={next => title('fontSize', `${next}px`)} /></label>
      <label>Peso da fonte<select value={value.title.fontWeight} onChange={e => title('fontWeight', Number(e.target.value))}><option value={400}>Normal</option><option value={600}>Seminegrito</option><option value={700}>Negrito</option></select></label>
      <label>Cor do título<input type="color" value={value.title.color} onChange={e => title('color', e.target.value)} /></label>
      <label>Fundo do título<input type="color" value={value.title.background} onChange={e => title('background', e.target.value)} /></label>
    </div></fieldset></section>
  </div></Panel>;
}
